import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import DialogActions from '@mui/material/DialogActions';
import { Box, Divider, FormHelperText, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { isNumber } from 'lodash';

import { getAllPopsState } from '../../../../../../features/pop/get_all_pops';
import TextFieldFormik from '../../../../../common/text_field';
import { Button, SubmitButton } from '../../../../../common/button';
import { createProductAvailabilityAction, createProductAvailabilityState } from '../../../../../../features/reservation/create_product_availability';
import useNotification from '../../../../../../core/hooks/use_notification';
import { createProductAvailabilityNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';

const ProductAvailabilityPopsForm = ({ data, setView, refreshCalendar, onClose }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const getAllPops = useSelector(getAllPopsState);
    const createProductAvailability = useSelector(createProductAvailabilityState);

    const initialValues = {
        pops: getAllPops?.data?.map(pop => ({
            id: pop?.id,
            quantityLimit: ''
        })) || []
    };

    const schema = Yup.object().shape({
        pops: Yup.array().of(
            Yup.object().shape({
                id: Yup.string().required(),
                quantityLimit: Yup.number().notRequired()
            })
        )
    });

    const onChangeQuantity = (quantity, formik) => {
        const pops = formik?.values?.pops?.map(item => ({ ...item, quantityLimit: parseFloat(quantity) || quantity }));
        formik.setFieldValue('pops', pops);
    };

    const onSubmit = (values, { setErrors }) => {
        const errors = validate(values);

        if (errors.pops) {
            setErrors(errors);
            return;
        }

        const popsQuantityLimits = values?.pops
            ?.filter((popQuantityLimit) => !!popQuantityLimit.quantityLimit)
            ?.reduce((accumulator, current) => {
                accumulator[current.id] = current.quantityLimit;
                return accumulator;
            }, {});

        const formToSend = {
            ...data?.form,
            popsQuantityLimits
        };

        dispatch(createProductAvailabilityAction(formToSend))
            .then(response => {
                showNotification(createProductAvailabilityNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    onClose();
                    refreshCalendar && refreshCalendar();
                }
            });
    };

    const validate = (values) => {
        const errors = { pops: '' };

        const popsQuantity = values?.pops
            ?.map(pop => pop?.quantityLimit)
            ?.reduce((accumulator, current) => {
                return isNumber(current) ? accumulator + current : accumulator;
            }, 0);

        const generalQuantityLimit = data?.form?.generalQuantityLimit;

        if (popsQuantity > generalQuantityLimit) {
            errors.pops = `Przydzielono popom ${ popsQuantity } szt., gdy globalny limit wynosi: ${ generalQuantityLimit } szt.`;
        }

        return errors;
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }>
            { (formik) => (
                <Form>
                    <Grid container spacing={ 3 }>
                        <Grid item xs={ 12 } sm={ 4 }>
                            <TextField
                                name="quantity"
                                label="Ustaw ilość dla wszystkich"
                                type="number"
                                fullWidth
                                onChange={ event => onChangeQuantity(event.target.value, formik) }
                            />
                        </Grid>

                        <Grid item xs={ 12 }>
                            <TableContainer component={ Paper } variant="outlined">
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                            <TableCell>Punkt odbioru (POP)</TableCell>
                                            <TableCell align="center">Limit rezerwacji</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <FieldArray name="pops">
                                            { () => (
                                                getAllPops?.data?.map((pop, index) => (
                                                    <TableRow key={ pop?.id } hover>
                                                        <TableCell>{ pop?.name }</TableCell>
                                                        <TableCell>
                                                            <TextFieldFormik
                                                                name={ `pops.${ index }.quantityLimit` }
                                                                type="number"
                                                                size="small"
                                                                inputProps={ { min: '1', step: '1' } }
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) }
                                        </FieldArray>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>

                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <FormHelperText error>
                            <ErrorMessage name="pops"/>
                        </FormHelperText>
                    </Box>

                    <Divider sx={ { mt: 3, mb: 2, mx: -3 } }/>

                    <DialogActions sx={ { padding: 0, gap: 1 } }>
                        <Button variant="outlined" onClick={ () => setView(0) }>Wróć</Button>
                        <SubmitButton isLoading={ createProductAvailability?.loading }>Zapisz</SubmitButton>
                    </DialogActions>
                </Form>
            ) }
        </Formik>
    );
};

export default ProductAvailabilityPopsForm;